import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  orderBy,
  updateDoc,
  Timestamp,
  connectFirestoreEmulator,
  getFirestore,
  enableNetwork,
  disableNetwork,
  waitForPendingWrites,
  onSnapshot
} from 'firebase/firestore';
import { db, getCurrentUserId, isAuthenticated, getCurrentUser, waitForAuthState } from '../firebase';
import { SaveData } from './localAdapter';
import { StorageAdapter } from './storageService';
import { saveProfileToFirebase, loadProfileFromFirebase, deleteProfileFromFirebase } from '../services/firebaseStorage';

/**
 * Retry a function with exponential backoff
 */
async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> {
  let lastError: Error | undefined;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;

      if (attempt < maxRetries - 1) {
        const delay = baseDelay * Math.pow(2, attempt);
        console.log(`Retry attempt ${attempt + 1} after ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError;
}


export class FirebaseAdapter implements StorageAdapter {
  private firestoreReady = false;
  private networkEnabled = false;
  private connectionPromise: Promise<void> | null = null;

  private getProfilesCollection(userId?: string) {
    const uid = userId || getCurrentUserId();
    if (!uid) {
      throw new Error('User not authenticated');
    }
    return collection(db, 'users', uid, 'profiles');
  }

  async init(): Promise<void> {
    console.log('🚀 [FirebaseAdapter] Initializing Firebase adapter...');

    // Wait for authentication state to be initialized
    console.log('⏳ [FirebaseAdapter] Waiting for authentication state...');
    const user = await waitForAuthState();

    if (!user) {
      console.error('❌ [FirebaseAdapter] No authenticated user found');
      throw new Error('Firebase adapter requires authentication. Please sign in and try again.');
    }

    const userId = getCurrentUserId();
    console.log(`✅ [FirebaseAdapter] Authenticated with user ID: ${userId}`);

    // If already initializing, wait for the existing promise
    if (this.connectionPromise) {
      console.log('⏳ [FirebaseAdapter] Already initializing, waiting for existing promise...');
      await this.connectionPromise;
      return;
    }

    // Reset state
    this.firestoreReady = false;
    this.networkEnabled = false;

    // Create connection promise
    this.connectionPromise = this.ensureFirestoreConnection();

    try {
      await this.connectionPromise;
      this.firestoreReady = true;
      console.log('🎉 [FirebaseAdapter] Firebase adapter initialized successfully');
    } catch (error) {
      this.connectionPromise = null;
      this.firestoreReady = false;
      this.networkEnabled = false;
      console.error('❌ [FirebaseAdapter] Failed to initialize Firebase adapter:', error);
      throw error;
    }
  }

  private async ensureFirestoreConnection(): Promise<void> {
    // Enable network connectivity
    try {
      await enableNetwork(db);
      this.networkEnabled = true;
    } catch (error) {
      console.warn('Failed to enable Firestore network:', error);
      // Continue anyway - might work with local cache
    }

    // Wait for Firestore to be ready by attempting a simple operation
    // This ensures connectivity is established
    await this.waitForConnection();
  }

  private async waitForConnection(maxRetries: number = 5): Promise<void> {
    console.log('🔍 [FirebaseAdapter] Testing Firestore connection...');

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Use a valid path within our security rules for connection testing
        const userId = getCurrentUserId();
        if (!userId) {
          console.warn(`🔍 [FirebaseAdapter] Attempt ${attempt}: No user ID available yet, waiting...`);
          await new Promise(resolve => setTimeout(resolve, 500));
          continue;
        }

        console.log(`🔍 [FirebaseAdapter] Attempt ${attempt}: Testing connection with user ID: ${userId}`);

        // Try a simple operation to test connectivity within our allowed path
        const testDocRef = doc(db, 'users', userId, 'connection_status', '_connection_test');
        await getDoc(testDocRef);
        console.log('✅ [FirebaseAdapter] Firestore connection established successfully');
        return; // Success - connection is established
      } catch (error) {
        const errorMessage = (error as Error).message.toLowerCase();
        console.warn(`🔍 [FirebaseAdapter] Attempt ${attempt} failed:`, errorMessage);

        // Check for permission denied - this indicates auth rules are working but user might not have access
        if (errorMessage.includes('permission-denied')) {
          console.log('🔒 [FirebaseAdapter] Permission denied - this is expected for test document, connection OK');
          return; // Permission denied is actually good - it means auth is working
        }

        // If it's an offline error and we have network enabled, wait and retry
        if ((errorMessage.includes('offline') || errorMessage.includes('unavailable') || errorMessage.includes('client is offline')) && this.networkEnabled) {
          if (attempt < maxRetries) {
            const delay = 1000 * attempt; // Exponential backoff
            console.log(`⏳ [FirebaseAdapter] Connection attempt ${attempt} failed, retrying in ${delay}ms...`);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }
        }

        // For other errors or if we've exhausted retries, throw
        console.error('❌ [FirebaseAdapter] Connection test failed:', error);
        throw error;
      }
    }
  }

  private async ensureFirestoreReady(): Promise<void> {
    console.log('🔧 [FirebaseAdapter] Ensuring Firestore is ready...');

    // First, ensure authentication state is initialized
    if (!isAuthenticated()) {
      console.log('⏳ [FirebaseAdapter] Waiting for authentication state...');
      const user = await waitForAuthState();
      if (!user) {
        console.warn('⚠️ [FirebaseAdapter] No authenticated user found');
        throw new Error('Authentication required. Please sign in and try again.');
      }
    }

    if (!this.firestoreReady) {
      console.log('🔄 [FirebaseAdapter] Firestore not ready, initializing...');
      await this.init();
    }

    // Double-check authentication after initialization
    if (!isAuthenticated()) {
      console.warn('⚠️ [FirebaseAdapter] User not authenticated after initialization');
      throw new Error('Please wait — connecting to cloud storage. Try again in a moment.');
    }

    console.log('✅ [FirebaseAdapter] Firestore ready for operations');
  }

  async listProfiles(): Promise<{id: string, name: string, updatedAt: Date, lastAccessedAt: Date}[]> {
    // Capture the authenticated user at the start of the operation
    const currentUser = getCurrentUser();
    if (!currentUser) {
      console.warn('⚠️ [FirebaseAdapter] No authenticated user available for list operation');
      return [];
    }

    const userId = currentUser.uid;
    console.log(`🔐 [FirebaseAdapter] Listing profiles for authenticated user ID: ${userId}`);

    try {
      await this.ensureFirestoreReady();

      // Query the profiles collection for metadata
      const profilesCollection = collection(db, 'users', userId, 'profiles');
      const q = query(profilesCollection, orderBy('lastAccessedAt', 'desc'));
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id, // The document ID is the profile ID
          name: data.name || doc.id,
          updatedAt: data.updatedAt?.toDate() || new Date(),
          lastAccessedAt: data.lastAccessedAt?.toDate() || new Date()
        };
      });
    } catch (error) {
      console.error('Failed to list Firebase profiles:', error);
      return [];
    }
  }

  async loadProfile(id: string): Promise<SaveData | null> {
    console.log(`📖 [FirebaseAdapter] Loading profile: ${id}`);

    // Capture the authenticated user at the start of the operation
    const currentUser = getCurrentUser();
    if (!currentUser) {
      console.warn('⚠️ [FirebaseAdapter] No authenticated user available for load operation');
      return null;
    }

    const userId = currentUser.uid;
    console.log(`🔐 [FirebaseAdapter] Loading profile for authenticated user ID: ${userId}`);

    try {
      const data = await loadProfileFromFirebase(userId, id);
      console.log('📖 [FirebaseAdapter] Loaded profile data:', {
        modelsCount: data?.models?.length || 0,
        itemsCount: data?.items?.length || 0,
        warbandName: data?.warband?.name
      });
      return data;
    } catch (error) {
      console.error('❌ [FirebaseAdapter] Failed to load Firebase profile:', error);
      return null;
    }
  }


  async saveProfile(id: string, data: SaveData, profileName?: string): Promise<void> {
    console.log(`💾 [FirebaseAdapter] Saving profile: ${profileName || id} (${id})`);

    // Capture the authenticated user at the start of the operation
    const currentUser = getCurrentUser();
    if (!currentUser) {
      console.error('❌ [FirebaseAdapter] No authenticated user available for save operation');
      throw new Error('User not authenticated');
    }

    const userId = currentUser.uid;
    console.log(`🔐 [FirebaseAdapter] Using authenticated user ID: ${userId}`);

    try {
      await retryWithBackoff(
        () => saveProfileToFirebase(userId, id, data, profileName || id),
        3, // max 3 retries
        1000 // start with 1 second delay
      );
      console.log(`✅ [FirebaseAdapter] Profile saved successfully: ${profileName || id}`);
    } catch (error) {
      console.error('❌ [FirebaseAdapter] Failed to save Firebase profile after retries:', error);
      throw error;
    }
  }

  async deleteProfile(id: string): Promise<void> {
    // Capture the authenticated user at the start of the operation
    const currentUser = getCurrentUser();
    if (!currentUser) {
      console.error('❌ [FirebaseAdapter] No authenticated user available for delete operation');
      throw new Error('User not authenticated');
    }

    const userId = currentUser.uid;
    console.log(`🔐 [FirebaseAdapter] Deleting profile for authenticated user ID: ${userId}`);

    try {
      await deleteProfileFromFirebase(userId, id);
    } catch (error) {
      console.error('Failed to delete Firebase profile:', error);
      throw error;
    }
  }

  async renameProfile(id: string, newName: string): Promise<void> {
    // TODO: This function needs to be updated for the new granular document structure
    // Renaming now involves moving data across multiple collections and document paths
    // For now, this is a placeholder implementation
    throw new Error('Rename profile not implemented for new granular structure. Please delete and recreate the profile with the new name.');
  }

  async exportProfile(id: string): Promise<void> {
    // Capture the authenticated user at the start of the operation
    const currentUser = getCurrentUser();
    if (!currentUser) {
      console.error('❌ [FirebaseAdapter] No authenticated user available for export operation');
      throw new Error('User not authenticated');
    }

    const userId = currentUser.uid;
    console.log(`🔐 [FirebaseAdapter] Exporting profile for authenticated user ID: ${userId}`);

    try {
      // Load the profile data using the new granular load function
      const profileData = await loadProfileFromFirebase(userId, id);

      if (!profileData) {
        throw new Error('Profile not found');
      }

      // Get profile metadata for the name
      const profileDoc = await getDoc(doc(db, 'users', userId, 'profiles', id));
      const profileName = profileDoc.exists() ? profileDoc.data()?.name || id : id;

      const exportData = {
        ...profileData,
        profileName: profileName,
        exportedAt: new Date().toISOString(),
        exportedFrom: 'firebase'
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json'
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${profileName.replace(/[^a-zA-Z0-9]/g, '_')}.json`;
      a.click();

      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export Firebase profile:', error);
      throw error;
    }
  }

  async importProfile(file: File): Promise<string> {
    // Capture the authenticated user at the start of the operation
    const currentUser = getCurrentUser();
    if (!currentUser) {
      console.error('❌ [FirebaseAdapter] No authenticated user available for import operation');
      throw new Error('User not authenticated');
    }

    const userId = currentUser.uid;
    console.log(`🔐 [FirebaseAdapter] Importing profile for authenticated user ID: ${userId}`);

    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = async (event) => {
        try {
          const result = event.target?.result;
          if (typeof result !== 'string') {
            throw new Error('Invalid file content');
          }

          const importData = JSON.parse(result);

          // Extract profile data
          const profileName = importData.profileName || 'Imported Profile';
          const profileId = profileName.toLowerCase().replace(/[^\w\s-]/g, '').replace(/[\s_-]+/g, '-').replace(/^-+|-+$/g, '');

          const data: SaveData = {
            warband: importData.warband,
            models: importData.models || [],
            items: importData.items || [],
            locations: importData.locations || [],
            modelTemplates: importData.modelTemplates || [],
            itemTemplates: importData.itemTemplates || []
          };

          // Check if profile already exists and generate unique ID if needed
          const profilesCollection = collection(db, 'users', userId, 'profiles');
          const existingDoc = await getDoc(doc(profilesCollection, profileId));

          let finalProfileId = profileId;
          let finalProfileName = profileName;

          if (existingDoc.exists()) {
            finalProfileId = `${profileId}_${Date.now()}`;
            finalProfileName = `${profileName} (Imported)`;
          }

          await this.saveProfile(finalProfileId, data, finalProfileName);
          resolve(finalProfileId);
        } catch (error) {
          console.error('Failed to import Firebase profile:', error);
          reject(error);
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      reader.readAsText(file);
    });
  }
}

// Export singleton instance
export const firebaseAdapter = new FirebaseAdapter();
